import { useEffect, useState } from 'react';
import { Loader2, MapPin, RefreshCw, Search } from 'lucide-react';
import { checkoutApi } from '../../api/client';

/**
 * Real bpost location selector — calls the admin/testMode-gated
 * `/api/checkout/address/locations` endpoint and renders the
 * sanitized result list as selectable cards.
 *
 * Behavioural contract:
 *  - Selector is rendered only inside the existing
 *    `manualPickupAllowed` branch (admin or ?testMode=1).
 *  - Default search basis = the delivery postal code & city
 *    captured from react-hook-form via `getDeliveryAddress()`.
 *  - On every method change OR delivery-address change, the local
 *    result list and selected card are cleared.
 *  - Backend returns `{available: false, configured: false}` when
 *    the bpost Locator key isn't configured yet → the component
 *    surfaces a calm hint and the manual entry below remains the
 *    source of truth.
 */
export function BpostLocationSearch({ method, getDeliveryAddress, onSelect, testMode = false }) {
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [available, setAvailable] = useState(null);   // null = unknown; true/false = known
  const [results, setResults] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const type = method === 'PARCEL_LOCKER' ? 'parcel_locker' : 'service_point';

  // Pre-fill from the current delivery-address fields once on mount /
  // whenever the user opens this branch with a fresh address.
  useEffect(() => {
    const cur = getDeliveryAddress ? getDeliveryAddress() : {};
    if (cur?.postalCode) setPostalCode(String(cur.postalCode).trim());
    if (cur?.city) setCity(String(cur.city).trim());
    // Clear stale results when the parent switches method (service
    // point ↔ parcel locker should never share selections).
    setResults([]);
    setSelectedId(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method]);

  const handleSearch = async () => {
    setError(null);
    setResults([]);
    setSelectedId(null);
    const pc = String(postalCode || '').trim();
    if (!/^\d{4}$/.test(pc)) {
      setError('Geldige Belgische postcode (4 cijfers) is vereist.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await checkoutApi.searchLocations({
        postalCode: pc, city, type, testMode,
      });
      setAvailable(Boolean(data?.available));
      if (data?.configured === false) {
        setError('bpost-keuzehulp is nog niet geconfigureerd op deze omgeving — gebruik manuele invoer hieronder.');
        return;
      }
      if (data?.error) {
        setError(`Geen resultaten (${data.error}). Probeer opnieuw of vul manueel in.`);
        return;
      }
      const list = Array.isArray(data?.locations) ? data.locations : [];
      setResults(list);
      if (list.length === 0) {
        setError('Geen punten gevonden voor deze postcode. Vul manueel in.');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'bpost-zoekopdracht mislukt.');
    } finally {
      setLoading(false);
    }
  };

  const handlePick = (loc) => {
    setSelectedId(loc.pointId);
    onSelect && onSelect(loc);
  };

  return (
    <div data-testid="bpost-location-search" className="rounded-xl border border-border bg-muted/40 px-4 py-3 space-y-3">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-muted-foreground" />
        <p className="text-sm font-semibold text-foreground">
          bpost {type === 'parcel_locker' ? 'Pakjesautomaat' : 'Afhaalpunt'} zoeken
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr_auto] gap-2">
        <input
          data-testid="bpost-search-postal"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
          placeholder="Postcode"
          className="px-3 py-2 rounded-lg border border-input bg-card text-sm"
        />
        <input
          data-testid="bpost-search-city"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="Stad (optioneel)"
          className="px-3 py-2 rounded-lg border border-input bg-card text-sm"
        />
        <button
          type="button"
          data-testid="bpost-search-btn"
          onClick={handleSearch}
          disabled={loading}
          className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center gap-1.5"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Zoek
        </button>
      </div>

      {error && (
        <p data-testid="bpost-search-error" className="text-xs text-amber-700">{error}</p>
      )}

      {available === false && !error && (
        <p data-testid="bpost-search-not-configured" className="text-xs text-muted-foreground">
          bpost-keuzehulp niet beschikbaar in deze omgeving.
        </p>
      )}

      {results.length > 0 && (
        <ul data-testid="bpost-search-results" className="space-y-2">
          {results.map((loc) => {
            const isSelected = selectedId === loc.pointId;
            return (
              <li key={loc.pointId}>
                <button
                  type="button"
                  data-testid={`bpost-search-result-${loc.pointId}`}
                  onClick={() => handlePick(loc)}
                  className={`w-full text-left px-3 py-2 rounded-lg border ${isSelected ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted/50'} transition-colors`}
                >
                  <div className="flex items-start gap-2">
                    <MapPin className={`w-4 h-4 mt-0.5 shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{loc.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {[loc.street, loc.houseNumber].filter(Boolean).join(' ')} · {loc.postalCode} {loc.city}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        id {loc.pointId}
                      </p>
                    </div>
                    {isSelected && (
                      <span className="text-[10px] font-semibold uppercase text-primary shrink-0">Gekozen</span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
